import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { chSupabase } from '../lib/supabase';
import { resolveChAccess, type ChAccessInfo } from '../lib/chAccess';
import { chProfileService } from '../services';
import type { ChProfile } from '../types';

interface ChAuthState {
  user: ChProfile | null;
  loading: boolean;
  accessInfo: ChAccessInfo;
  signOut: () => Promise<void>;
}

const ChAuthContext = createContext<ChAuthState | null>(null);

export function ChAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<ChProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessInfo, setAccessInfo] = useState<ChAccessInfo>({
    role: null,
    isAgencyStaff: false,
    hasAccess: false,
  });

  useEffect(() => {
    let mounted = true;

    async function loadProfile() {
      try {
        const { data: { session } } = await chSupabase.auth.getSession();
        if (!session || !mounted) {
          if (mounted) setLoading(false);
          return;
        }

        const access = await resolveChAccess();
        if (!mounted) return;
        setAccessInfo(access);

        if (access.hasAccess) {
          const profile = await chProfileService.getCurrent();
          if (mounted) setUser(profile);
        }
      } catch {
        // no-op
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadProfile();

    const { data: { subscription } } = chSupabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN') {
        (async () => {
          const access = await resolveChAccess();
          if (!mounted) return;
          setAccessInfo(access);
          if (access.hasAccess) {
            const profile = await chProfileService.getCurrent();
            if (mounted) setUser(profile);
          }
        })();
      } else if (event === 'SIGNED_OUT') {
        if (mounted) {
          setUser(null);
          setAccessInfo({ role: null, isAgencyStaff: false, hasAccess: false });
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await chSupabase.auth.signOut();
    setUser(null);
    setAccessInfo({ role: null, isAgencyStaff: false, hasAccess: false });
  };

  return (
    <ChAuthContext.Provider value={{ user, loading, accessInfo, signOut }}>
      {children}
    </ChAuthContext.Provider>
  );
}

export function useChAuth() {
  const ctx = useContext(ChAuthContext);
  if (!ctx) throw new Error('useChAuth must be used within ChAuthProvider');
  return ctx;
}
